import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Plus, Search, X } from "lucide-react";
import type { Filter } from "@cd/core";
import { api, type Catalog, type CatalogDimension } from "../../lib/api";
import { cn } from "../../lib/utils";
import { Input } from "../../components/ui/primitives";
import { metricRefsOf, useBuilder } from "./store";

type Value = Filter["values"][number];

/** How many of a field's values the list offers, most-used first. */
const VALUE_LIMIT = 200;

/**
 * A field's values over the last 90 days, ranked by a metric that can be split by it: the widget's
 * own when it has one that can, else the first in the pack that can. None when no metric can.
 */
function useFieldValues(catalog: Catalog, field: CatalogDimension, locationIds?: string[]) {
  const { draft } = useBuilder();
  const canSplit = (m: string) => catalog.compatibility[m]?.includes(field.ref);
  const metric = metricRefsOf(draft.metrics).find(canSplit) ?? catalog.metrics.find((m) => canSplit(m.ref))?.ref;
  const query = useQuery({
    queryKey: ["filterValues", draft.pack, field.ref, metric, locationIds],
    queryFn: () =>
      api.query(
        {
          pack: draft.pack,
          metrics: [{ kind: "metric", ref: metric! }],
          dimensions: [{ ref: field.ref }],
          sort: [{ key: metric!, dir: "desc" }],
          limit: VALUE_LIMIT,
          timePreset: "last_90_days",
        },
        locationIds,
        { source: "builder" },
      ),
    enabled: !!metric,
    staleTime: 5 * 60_000,
  });
  const values = query.data?.rows.map((r) => r[field.ref]).filter((v): v is Value => v !== null && v !== undefined) ?? [];
  return { values, loading: query.isLoading && !!metric, failed: !!query.error || !metric };
}

/**
 * Values for an "is any of" / "is none of" filter: tick them from the field's values, or type one
 * that isn't listed.
 */
export function FilterValues({ catalog, field, values, onChange, locationIds }: {
  catalog: Catalog;
  field: CatalogDimension;
  values: Value[];
  onChange: (values: Value[]) => void;
  locationIds?: string[];
}) {
  const [q, setQ] = useState("");
  const { values: options, loading, failed } = useFieldValues(catalog, field, locationIds);
  const isNumber = field.type === "number";

  const chosen = new Set(values.map(String));
  const toggle = (v: Value) => onChange(chosen.has(String(v)) ? values.filter((x) => String(x) !== String(v)) : [...values, v]);
  const typed = q.trim();
  const typedValue: Value | null = !typed ? null : isNumber ? (Number.isFinite(Number(typed)) ? Number(typed) : null) : typed;
  const needle = typed.toLowerCase();
  const shown = options.filter((v) => String(v).toLowerCase().includes(needle));
  const canAddTyped = typedValue !== null && !options.some((v) => String(v) === String(typedValue)) && !chosen.has(String(typedValue));
  const addTyped = () => {
    if (typedValue === null) return;
    if (!chosen.has(String(typedValue))) onChange([...values, typedValue]);
    setQ("");
  };

  return (
    <div className="space-y-1.5">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((v) => (
            <span key={String(v)} className="flex max-w-full items-center gap-1 rounded-md bg-accent-soft px-1.5 py-0.5 text-xs">
              <span className="truncate">{String(v)}</span>
              <button onClick={() => toggle(v)} className="text-subtle hover:text-foreground" aria-label={`Remove ${String(v)}`}>
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2 size-3 -translate-y-1/2 text-subtle" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            // Enter picks the only match, else adds what was typed.
            if (shown.length === 1 && !canAddTyped) toggle(shown[0]!);
            else addTyped();
            setQ("");
          }}
          placeholder={failed ? "Type a value and press Enter" : `Search ${field.label.toLowerCase()}`}
          className="h-7 pl-6 text-xs"
        />
      </div>
      {!failed && (
        <div className="max-h-44 overflow-y-auto rounded-lg border border-border bg-surface py-0.5">
          {loading && <p className="px-2 py-1.5 text-xs text-subtle">Loading values…</p>}
          {!loading && !options.length && <p className="px-2 py-1.5 text-xs text-subtle">No values in the last 90 days. Type one and press Enter.</p>}
          {canAddTyped && (
            <button onClick={addTyped} className="flex w-full items-center gap-2 px-2 py-1 text-left text-xs hover:bg-surface-2">
              <Plus className="size-3 shrink-0 text-subtle" />
              <span className="truncate">Add “{String(typedValue)}”</span>
            </button>
          )}
          {shown.map((v) => {
            const on = chosen.has(String(v));
            return (
              <button key={String(v)} onClick={() => toggle(v)} className="flex w-full items-center gap-2 px-2 py-1 text-left text-xs hover:bg-surface-2">
                <span className={cn("flex size-3.5 shrink-0 items-center justify-center rounded border", on ? "border-accent bg-accent text-white" : "border-border-strong")}>
                  {on && <Check className="size-2.5" strokeWidth={3} />}
                </span>
                <span className="truncate">{String(v)}</span>
              </button>
            );
          })}
        </div>
      )}
      {!failed && !loading && options.length >= VALUE_LIMIT && (
        <p className="text-[11px] text-subtle">Showing the top {VALUE_LIMIT}. Search or type to add others.</p>
      )}
    </div>
  );
}
